import { useEffect, useMemo, useRef, useState } from "react";
import { useApp } from "@/context/AppContext";
import { authService } from "@/lib/services";
import type { MembershipRole, MembershipService, Team, TeamMemberGrant, UserMember, UserSearchResult } from "@/types";

/** Extract a string `detail` from an API error, if present. */
function errorDetail(err: unknown): string | null {
  const d = (err as { data?: { detail?: unknown } } | undefined)?.data?.detail;
  return typeof d === "string" ? d : null;
}

export interface UseMembershipManagerOptions {
  service: MembershipService;
  entityUuid: string;
  /** Console log prefix + used for the generic load-error message. */
  loadErrorLabel: string;
  /** Optional hooks (e.g. agents fire an extension-impact check on add). */
  onAfterAddUser?: (userUuid: string) => void;
  onAfterAddTeam?: (teamUuid: string) => void;
}

/**
 * All the membership state + effects + CRUD handlers shared by role-based
 * access tabs (agents, micro apps, ...). Domain-specific copy (role option
 * descriptions, header text) and extras (agent extension-impact) live in the
 * caller.
 */
export function useMembershipManager({
  service,
  entityUuid,
  loadErrorLabel,
  onAfterAddUser,
  onAfterAddTeam,
}: UseMembershipManagerOptions) {
  const { teams } = useApp();

  const [userMembers, setUserMembers] = useState<UserMember[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMemberGrant[]>([]);
  const [canManage, setCanManage] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [userResults, setUserResults] = useState<UserSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const userMembersRef = useRef(userMembers);
  userMembersRef.current = userMembers;

  // Load membership (ignore-guarded against entity switch / unmount).
  useEffect(() => {
    let ignore = false;
    setIsLoading(true);
    setError(null);
    void (async () => {
      try {
        const response = await service.getMembers(entityUuid);
        if (ignore) return;
        setUserMembers(response.data.userMembers);
        setTeamMembers(response.data.teamMembers);
        setCanManage(response.data.canManage);
      } catch (err) {
        if (ignore) return;
        console.error(`${loadErrorLabel}:`, err);
        setError("Failed to load access settings");
      } finally {
        if (!ignore) setIsLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [service, entityUuid, loadErrorLabel]);

  // Debounced user search.
  useEffect(() => {
    if (query.trim().length < 1) {
      setUserResults([]);
      return;
    }
    // Per-effect-run ignore flag: the debounce guards *send* order, not
    // *arrival* order — an older in-flight request must not overwrite results
    // for a newer query when it resolves last.
    let ignore = false;
    const timer = setTimeout(async () => {
      try {
        setIsSearching(true);
        const response = await authService.searchUsers(query);
        if (ignore) return;
        const existing = new Set(userMembersRef.current.map((m) => m.user.uuid));
        setUserResults(response.data.filter((u) => !existing.has(u.uuid)));
      } catch (err) {
        console.error("Failed to search users:", err);
      } finally {
        if (!ignore) setIsSearching(false);
      }
    }, 300);
    return () => {
      ignore = true;
      clearTimeout(timer);
    };
  }, [query]);

  const memberTeamUuids = useMemo(() => new Set(teamMembers.map((m) => m.teamUuid)), [teamMembers]);
  const teamResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    const addable = teams.filter((t) => !memberTeamUuids.has(t.uuid));
    if (!q) return addable;
    return addable.filter((t) => t.name.toLowerCase().includes(q));
  }, [query, teams, memberTeamUuids]);

  const resetSearch = () => {
    setQuery("");
    setUserResults([]);
    setShowResults(false);
  };

  const runAction = async (id: string, action: () => Promise<void>, fallback: string) => {
    try {
      setBusyId(id);
      setError(null);
      await action();
    } catch (err) {
      setError(errorDetail(err) ?? fallback);
    } finally {
      setBusyId(null);
    }
  };

  // -- Users --------------------------------------------------------------
  const addUser = (user: UserSearchResult) =>
    runAction(
      user.uuid,
      async () => {
        const response = await service.addUserMember(entityUuid, user.uuid, "viewer");
        setUserMembers((prev) => [response.data, ...prev]);
        resetSearch();
        onAfterAddUser?.(user.uuid);
      },
      "Failed to add member",
    );

  const changeUserRole = (userUuid: string, role: MembershipRole) =>
    runAction(
      userUuid,
      async () => {
        const response = await service.updateUserMemberRole(entityUuid, userUuid, role);
        setUserMembers((prev) => prev.map((m) => (m.user.uuid === userUuid ? response.data : m)));
      },
      "Failed to change role",
    );

  const removeUser = (userUuid: string) =>
    runAction(
      userUuid,
      async () => {
        await service.removeUserMember(entityUuid, userUuid);
        setUserMembers((prev) => prev.filter((m) => m.user.uuid !== userUuid));
      },
      "Failed to remove member",
    );

  // -- Teams --------------------------------------------------------------
  const addTeam = (team: Team) =>
    runAction(
      team.uuid,
      async () => {
        const response = await service.addTeamMember(entityUuid, team.uuid, "viewer");
        setTeamMembers((prev) => [response.data, ...prev]);
        resetSearch();
        onAfterAddTeam?.(team.uuid);
      },
      "Failed to add team",
    );

  const changeTeamRole = (teamUuid: string, role: MembershipRole) =>
    runAction(
      teamUuid,
      async () => {
        const response = await service.updateTeamMemberRole(entityUuid, teamUuid, role);
        setTeamMembers((prev) => prev.map((m) => (m.teamUuid === teamUuid ? response.data : m)));
      },
      "Failed to change role",
    );

  const removeTeam = (teamUuid: string) =>
    runAction(
      teamUuid,
      async () => {
        await service.removeTeamMember(entityUuid, teamUuid);
        setTeamMembers((prev) => prev.filter((m) => m.teamUuid !== teamUuid));
      },
      "Failed to remove team",
    );

  return {
    userMembers,
    teamMembers,
    canManage,
    isLoading,
    error,
    query,
    setQuery,
    userResults,
    isSearching,
    showResults,
    setShowResults,
    busyId,
    teamResults,
    addUser,
    changeUserRole,
    removeUser,
    addTeam,
    changeTeamRole,
    removeTeam,
  };
}
